import { Injectable, OnDestroy } from '@angular/core';
import moment, { Moment } from 'moment';
import { Carte } from 'src/app/interface/carte';
import { Checklist } from 'src/app/interface/checklist';
import { ChecklistItem, ChecklistModel } from 'src/app/interface/checklist-model';
import { CustomState } from 'src/app/interface/custom-state.model';
import { CachedKeys } from 'src/app/interface/data-cache-config';
import { Operation } from 'src/app/interface/operation';
import { PartBrand } from 'src/app/interface/part-brand';
import { PaymentCondition } from 'src/app/interface/paymentCondition';
import { RoType } from 'src/app/interface/ro-type';
import { Supplier } from 'src/app/interface/supplier';
import { Survey } from 'src/app/interface/survey';
import { TitleType } from 'src/app/interface/title-type';
import { MkgUser } from 'src/app/interface/user';
import { CarteService } from 'src/app/service/carte.service';
import { ChecklistModelItemServiceService } from 'src/app/service/checklist-model-item-service.service';
import { ChecklistModelService } from 'src/app/service/checklist-model.service';
import { ChecklistService } from 'src/app/service/checklist.service';
import { CustomStateService } from 'src/app/service/custom-state.service';
import { OperationService } from 'src/app/service/operation.service';
import { PartBrandService } from 'src/app/service/part-brand.service';
import { PaymentService } from 'src/app/service/payment.service';
import { RoTypeService } from 'src/app/service/ro-type.service';
import { SupplierService } from 'src/app/service/supplier.service';
import { SurveyService } from 'src/app/service/survey.service';
import { TitleTypeService } from 'src/app/service/title-type.service';
import { UserService } from 'src/app/service/user.service';

type SetChacheFromParamsOptions =  "carte" | "checklistByModel" | "checklist" | "operation" | "roType" | "user" | "all" | "survey" | "roState" | "budgetState";

@Injectable({
  providedIn: "root"
})
export class CachedOsService implements OnDestroy {

  private _configs = new Map<string, any[]>();
  lastSyncWithDB: Moment;
  
  checklistModel: ChecklistModel;
  checklistItem: ChecklistItem;
  paymentConditions: PaymentCondition[];
  partBrands: PartBrand[];
  titleTypes: TitleType[];
  cardAdms: Supplier[];

  constructor(
    private _carteService: CarteService,
    private _checklistModelService: ChecklistModelService,
    private _checklistService: ChecklistService,
    private _operationService: OperationService,
    private _roTypeService: RoTypeService,
    private _surveyService: SurveyService,
    private _chItemService: ChecklistModelItemServiceService,
    private _chModelService: ChecklistModelService,
    private _userService: UserService,
    private _customStateService: CustomStateService,
    private _paymentConditionService: PaymentService,
    private _titleTypeService: TitleTypeService,
    private _partBrandService: PartBrandService,
    private _supplierService: SupplierService
  ) { 
  }

  ngOnDestroy(): void {
    this._configs.clear();
  }

  async setAllCached({ setCacheFrom }: { setCacheFrom: SetChacheFromParamsOptions }) {
    if (this._checkLastFetch() && setCacheFrom === "all") {
      return;
    }

    this.lastSyncWithDB = moment();

    switch (setCacheFrom) {
      case 'carte':
        this._setAllCartes();
        break;
      case 'checklistByModel':
        this._setAllChecklistModels();
        break;
      case 'checklist':
        this._setAllChecklists();
        break;
      case 'operation':
        this._setAllOperations();
        break;
      case 'roType':
        this._setAllRoTypes();
        break;
      case 'user':
        this._setAllUsers();
        break;
      case 'survey':
        this._setAllSurveys();
        break;
      case 'roState':
        this.setRoBudgetStates("ro");
        break;
      case 'budgetState':
        this.setRoBudgetStates("budget");
        break;
      case 'all':
      default: 
        this._setAllCartes();
        this._setAllChecklistModels();
        this._setAllChecklists();
        this._setAllOperations();
        this._setAllRoTypes();
        this._setAllUsers();
        this._setAllSurveys();
        this.setRoBudgetStates("budget");
        this.setRoBudgetStates("ro");
    }
  }
  
  async getCachedData(singleItem?: CachedKeys) {
    if (singleItem) {
      const cachedData = this._configs.get(singleItem);

      if (cachedData) return cachedData;

      switch (singleItem) {
        case CachedKeys.ALL_CARTES:
          return await this._carteService.getAll();
        case CachedKeys.ALL_CHECKLISTS:
          return await this._checklistService.getAll();
        case CachedKeys.ALL_MODEL_CHECKLISTS:
          return await this._checklistModelService.getAll();
        case CachedKeys.ALL_OPERATIONS:
          return await this._operationService.getAll();
        case CachedKeys.ALL_RO_TYPES:
          return await this._roTypeService.getAll();
        case CachedKeys.ALL_USERS:
          return await this._userService.getAll();
        case CachedKeys.ALL_SURVEYS:
          return await this._surveyService.getAll();
        case CachedKeys.ALL_RO_STATES:
          return await this._customStateService.getAll();
        case CachedKeys.ALL_BUDGET_STATES:
          return await this._customStateService.getAllBudget();
      }
    }
    
    let [
      cartes,
      modelChecklists,
      checklists,
      operations,
      roTypes,
      users,
      surveys,
      budgetStates,
      roStates,
    ] = [
      this._configs.get(CachedKeys.ALL_CARTES),
      this._configs.get(CachedKeys.ALL_MODEL_CHECKLISTS),
      this._configs.get(CachedKeys.ALL_CHECKLISTS),
      this._configs.get(CachedKeys.ALL_OPERATIONS),
      this._configs.get(CachedKeys.ALL_RO_TYPES),
      this._configs.get(CachedKeys.ALL_USERS),
      this._configs.get(CachedKeys.ALL_SURVEYS),
      this._configs.get(CachedKeys.ALL_BUDGET_STATES),
      this._configs.get(CachedKeys.ALL_RO_STATES),
    ];

    if (!cartes) {
      cartes = await this._carteService.getAll();
      this._setAllCartes(cartes);
    }

    if (!modelChecklists) {
      modelChecklists = await this._checklistModelService.getAll();
      this._setAllChecklistModels(modelChecklists);
    }

    if (!checklists) {
      checklists = await this._checklistService.getAll();
      this._setAllChecklists(checklists);
    }

    if (!operations) {
      operations = await this._operationService.getAll();
      this._setAllOperations(operations);
    }

    if (!roTypes) {
      roTypes = await this._roTypeService.getAll();
      this._setAllRoTypes(roTypes);
    }

    if (!users) {
      users = await this._userService.getAll();
      this._setAllUsers(users);
    }

    if (!surveys) {
      surveys = await this._surveyService.getAll();
      this._setAllSurveys(surveys);
    }

    if (!budgetStates) {
      budgetStates = await this._customStateService.getAllBudget();
      this.setRoBudgetStates("budget", budgetStates);
    }

    if (!roStates) {
      roStates = await this._customStateService.getAll();
      this.setRoBudgetStates("ro", roStates);
    }

    if (!this._checkLastFetch()) {
      this.setAllCached({ setCacheFrom: "all" });
    }
    
    return [
      cartes,
      modelChecklists,
      checklists,
      operations,
      roTypes,
      users,
      surveys,
      budgetStates,
      roStates,
    ]
  }
  
  private _setAllCartes(alreadyFetchedData?: Carte[]) {
    if (alreadyFetchedData && alreadyFetchedData.length) {
      this._configs.set(CachedKeys.ALL_CARTES, alreadyFetchedData);
      return;
    }
    
    try {
      this._carteService.getAll()
        .then(cartes => this._configs.set(CachedKeys.ALL_CARTES, cartes));
    } catch (error) {
      console.warn("Não foi possível setar cache para carte");
      console.error(error);
    }
  }
  
  private _setAllChecklistModels(alreadyFetchedData?: ChecklistModel[]) {
    if (alreadyFetchedData && alreadyFetchedData.length) {
      this._configs.set(CachedKeys.ALL_MODEL_CHECKLISTS, alreadyFetchedData);
      return;
    }
    
    try {
      this._checklistModelService.getAll()
        .then(checklistModels => this._configs.set(CachedKeys.ALL_MODEL_CHECKLISTS, checklistModels));
    } catch (error) {
      console.warn("Não foi possível setar cache para checklistModel");
      console.error(error);
    }
  }
  
  private _setAllChecklists(alreadyFetchedData?: Checklist[]) {
    if (alreadyFetchedData && alreadyFetchedData.length) {
      this._configs.set(CachedKeys.ALL_CHECKLISTS, alreadyFetchedData);
      return;
    }
    
    try {
      this._checklistService.getAll()
        .then(checklists => this._configs.set(CachedKeys.ALL_CHECKLISTS, checklists));
    } catch (error) {
      console.warn("Não foi possível setar cache para checklist");
      console.error(error);
    }
  }
  
  
  private _setAllOperations(alreadyFetchedData?: Operation[]) {
    if (alreadyFetchedData && alreadyFetchedData.length) {
      this._configs.set(CachedKeys.ALL_OPERATIONS, alreadyFetchedData);
      return;
    }
    
    try {
      this._operationService.getAll()
        .then(operations => this._configs.set(CachedKeys.ALL_OPERATIONS, operations));
    } catch (error) {
      console.warn("Não foi possível setar cache para operations");
      console.error(error);
    }
  }
  
  private _setAllRoTypes(alreadyFetchedData?: RoType[]) {
    if (alreadyFetchedData && alreadyFetchedData.length) {
      this._configs.set(CachedKeys.ALL_RO_TYPES, alreadyFetchedData);
      return;
    }
    
    try {
      this._roTypeService.getAll()
        .then(roTypes => this._configs.set(CachedKeys.ALL_RO_TYPES, roTypes));
    } catch (error) {
      console.warn("Não foi possível setar cache para roTypes");
      console.error(error);
    }
  }
  
  private _setAllUsers(alreadyFetchedData?: MkgUser[]) {
    if (alreadyFetchedData && alreadyFetchedData.length) {
      this._configs.set(CachedKeys.ALL_USERS, alreadyFetchedData);
      return;
    }
    
    try {
      this._userService.getAll()
        .then(users => this._configs.set(CachedKeys.ALL_USERS, users));
    } catch (error) {
      console.warn("Não foi possível setar cache para users");
      console.error(error);
    }
  }

  private _setAllSurveys(alreadyFetchedData?: Survey[]) {
    if (alreadyFetchedData && alreadyFetchedData.length) {
      this._configs.set(CachedKeys.ALL_SURVEYS, alreadyFetchedData);
      return;
    }
    
    try {
      this._surveyService.getAll()
        .then(surveys => this._configs.set(CachedKeys.ALL_SURVEYS, surveys));
    } catch (error) {
      console.warn("Não foi possível setar cache para users");
      console.error(error);
    }
  }

  private _checkLastFetch(): boolean {
    return this.lastSyncWithDB?.clone() && (this.lastSyncWithDB?.clone()?.add(10, "minutes").isAfter(moment(), "minutes"));
  }
  
  async setRoBudgetStates(type: "ro" | "budget", alreadyFetchedData?: CustomState[]): Promise<CustomState[]> {
    if (this._checkLastFetch() && !alreadyFetchedData) {
      return;
    }
    
    if (type === "ro") {
      if (alreadyFetchedData) {
        this._configs.set(CachedKeys.ALL_RO_STATES, alreadyFetchedData);
      } else {
        this._customStateService.getAll()
          .then(states => this._configs.set(CachedKeys.ALL_RO_STATES, states));
      }
    } else if (type === "budget") {
      if (alreadyFetchedData) {
        this._configs.set(CachedKeys.ALL_BUDGET_STATES, alreadyFetchedData);
      } else {
        this._customStateService.getAll()
          .then(states => this._configs.set(CachedKeys.ALL_BUDGET_STATES, states));
      }
    }
  }

  clearCache() {
    this._configs.clear();
    this.lastSyncWithDB = undefined;
    this.clearPaymentConditions();
    this.clearChecklists();
    this.clearPartBrands();
    this.clearTitleTypes();
    this.clearCardAdms();
  }

  async setChecklistModel(checklistModelId: string, forceUpdate: boolean) {
    let chModel: ChecklistModel;
    if (this.checklistModel && !forceUpdate) {
      chModel = this.checklistModel;
    } else {
      chModel = await this._chModelService.get(checklistModelId);
      this.checklistModel = chModel;
    }

    return chModel;
  }

  async setChecklistItem(checklistItemId: string, forceUpdate: boolean) {
    let chItem: ChecklistItem;
    if (this.checklistModel && !forceUpdate) {
      chItem = this.checklistItem;
    } else {
      chItem = await this._chItemService.getById(checklistItemId);
      this.checklistItem = chItem;
    }

    return chItem;
  }

  async getPaymentConditions() {
    if (!this.paymentConditions || !this.paymentConditions?.length) {
      await this.setPaymentConditions();
    }

    return this.paymentConditions;
  }

  async setPaymentConditions(alreadyFetchedData?: PaymentCondition[]) {
    if (alreadyFetchedData && alreadyFetchedData.length) {
      this.paymentConditions = alreadyFetchedData;
    } else {
      const allPaymentsConditions = await this._paymentConditionService.getAll();
      this.paymentConditions = allPaymentsConditions.filter(paymentCondition => paymentCondition.available === 1);
    }
  }

  async getPartBrands() {
    if (!this.partBrands || !this.partBrands?.length) {
      await this.setPartBrands();
    }

    return this.partBrands;
  }

  async setPartBrands(alreadyFetchedData?: PaymentCondition[]) {
    if (alreadyFetchedData && alreadyFetchedData.length) {
      this.partBrands = alreadyFetchedData;
    } else {
      this.partBrands = await this._partBrandService.getAll();
    }
  }

  async getTitleTypes() {
    if (!this.titleTypes || !this.titleTypes?.length) {
      await this.setTitleTypes();
    }

    return this.titleTypes;
  }

  async setTitleTypes(alreadyFetchedData?: PaymentCondition[]) {
    if (alreadyFetchedData && alreadyFetchedData.length) {
      this.titleTypes = alreadyFetchedData;
    } else {
      this.titleTypes = await this._titleTypeService.getAll();
    }
  }

  async getCardAdms() {
    if (!this.cardAdms || !this.cardAdms?.length) {
      await this.setCardAdms();
    }

    return this.cardAdms;
  }

  async setCardAdms(alreadyFetchedData?: Supplier[]) {
    if (alreadyFetchedData && alreadyFetchedData.length) {
      this.cardAdms = alreadyFetchedData;
    } else {
      this.cardAdms = await this._supplierService.getCardAdmins();
    }
  }
  
  clearPartBrands() {
    this.partBrands = undefined;
  }

  clearPaymentConditions() {
    this.paymentConditions = undefined;
  }

  clearTitleTypes() {
    this.titleTypes = undefined;
  }

  clearCardAdms() {
    this.cardAdms = undefined;
  }

  clearChecklists() {
    this.checklistModel = undefined;
    this.checklistItem = undefined;
  }
}